import { isEmptyObject } from "helpers/validations";
import { IMAGE_BASE_URL } from "constants/configConstants";
import TrainIcon from "@Icons/TrainIcon";
import WalkingIcon from "@Icons/WalkingIcon";
import DriveIcon from "@Icons/DriveIcon";
import styles from "./DistanceStripDesktop.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import CrossIcon from "@Icons/CrossIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import sortModesByDuration from "@Utils/getConvertedMinutes";
import Image from "@Components/Image";
import { showInKmsOrMiles } from "@Pages/PropertyPage/components/MapSectionV3/utils/utils";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";

const iconProps: any = {
  "Bus/Train": {
    stroke: "#0e9f6e",
    height: "16",
    width: "16",
    strokeWidth: ".5",
  },
  Walking: {
    stroke: "#3e83f8",
    height: "16",
    width: "16",
    strokeWidth: ".5",
  },
  Driving: {
    stroke: "#c27803",
    height: "16",
    width: "16",
    strokeWidth: ".5",
  },
};

const getIcon = (iconUrl: string, mode: string) => {
  if (iconUrl === "train.svg") {
    return <TrainIcon {...iconProps[mode]} />;
  }
  if (iconUrl === "walk.svg") {
    return <WalkingIcon {...iconProps[mode]} />;
  }
  if (iconUrl === "drive.svg") {
    return <DriveIcon {...iconProps[mode]} />;
  }
  // eslint-disable-next-line react/forbid-elements
  return <Image alt={mode} src={IMAGE_BASE_URL + iconUrl} height={16} width={16} />;
};

const DistanceItem = ({ color, mode, iconUrl, distance, duration }: any) => (
  <div className={styles.distanceItem}>
    <div className={styles.avatarContainer}>
      <div className={`${styles.avatar} ${styles[color]}`}>{getIcon(iconUrl, mode)}</div>
    </div>
    <div className={styles.distanceText}>
      <div className={styles.distanceText} data-testid={`searchPage-map-${color}-distance`}>{`${
        distance || "na"
      }`}</div>
      <div className={styles.distanceSubText} data-testid={`searchPage-map-${color}-duration`}>{`(${
        duration || "na"
      })`}</div>
    </div>
  </div>
);

export const DistanceStripDesktop = ({
  displayText,
  selectedDistanceObj,
  className = "",
  setShowDistanceCard,
  showMiles = true,
  t,
}: any) => {
  const userLanguage = useSelector(({ appData }: AppState) => appData.userLanguage);
  if (isEmptyObject(selectedDistanceObj)) {
    return null;
  }
  const distanceObject = selectedDistanceObj;

  Object.values(distanceObject).forEach((obj: any) => {
    if (obj.duration)
      obj.duration = obj.duration.replace(/\bhours?\b/g, "h").replace(/\bmins?\b/g, "m");
  });
  // Sort the transportation modes based on duration.
  const sortedTransportModes = sortModesByDuration(distanceObject);

  // Map each transportation mode to its respective JSX component.
  const transportComponents: Record<string, JSX.Element | null> = {
    DRIVING: distanceObject["DRIVING"] ? (
      <DistanceItem
        mode="Driving"
        iconUrl="drive.svg"
        color="driving"
        distance={showInKmsOrMiles(distanceObject["DRIVING"].distance, showMiles, t)}
        duration={distanceObject["DRIVING"].duration}
      />
    ) : null,
    TRANSIT: distanceObject["TRANSIT"] ? (
      <DistanceItem
        mode="Bus/Train"
        iconUrl="train.svg"
        color="train"
        distance={showInKmsOrMiles(distanceObject["TRANSIT"].distance, showMiles, t)}
        duration={distanceObject["TRANSIT"].duration}
      />
    ) : null,
    WALKING: distanceObject["WALKING"] ? (
      <DistanceItem
        mode="Walking"
        iconUrl="walk.svg"
        color="walk"
        distance={showInKmsOrMiles(distanceObject["WALKING"].distance, showMiles, t)}
        duration={distanceObject["WALKING"].duration}
      />
    ) : null,
  };

  return (
    <div
      className={formatClassNames(
        styles.stripContainer,
        className,
        !showMiles && styles.styleForKms,
        userLanguage === "de" && styles.wider,
      )}
      data-testid="searchPage-distance-banner"
    >
      <div className={styles.headingContainer}>
        <div className={styles.heading} data-testid="searchPage-distance-banner-header-text">
          {t(displayText)}
        </div>
        <div role="none" className={styles.crossIcon} onClick={() => setShowDistanceCard(false)}>
          <CrossIcon />
        </div>
      </div>
      <div className={styles.gridStripItem}>
        {sortedTransportModes.map((mode) => transportComponents[mode])}
      </div>
    </div>
  );
};

export default wrapperHOC(DistanceStripDesktop, {
  componentName: "PropertyPage-DistanceStripDesktop",
  showForChina: true,
});
